/**
 * Sentry Monitoring Configuration
 * Initializes error tracking and performance monitoring
 */

import * as Sentry from '@sentry/browser';
import logger from '../core/logger.js';

/**
 * Configuration for Sentry initialization
 */
export interface SentryConfig {
  /** Sentry DSN */
  dsn?: string;
  /** Environment name (production, development, etc.) */
  environment?: string;
  /** Application version/release */
  release?: string;
}

/**
 * Error context for capturing exceptions
 */
export interface ErrorContext {
  [key: string]: unknown;
}

let sentryInitialized = false;

/**
 * Initialize Sentry error monitoring
 */
export function initSentry(config: SentryConfig = {}): void {
  // Skip initialization if already initialized
  if (sentryInitialized) {
    logger.warn('Sentry already initialized');
    return;
  }

  // Skip if no DSN provided or in development mode
  const isDevelopment = config.environment === 'development';
  if (!config.dsn || isDevelopment) {
    logger.info('Sentry disabled in development mode or DSN not configured');
    return;
  }

  try {
    Sentry.init({
      dsn: config.dsn,
      environment: config.environment || 'production',
      release: config.release || '1.0.0',
      
      // Sample rate for error events (100% = all errors)
      sampleRate: 1.0,
      
      // Sample rate for performance monitoring (10% of transactions)
      tracesSampleRate: 0.1,
      
      // Debug mode only in non-production
      debug: false,
      
      // Enable performance monitoring
      integrations: [
        Sentry.browserTracingIntegration(),
      ],
      
      // Filter sensitive data before sending
      beforeSend(event) {
        // Remove any localStorage or user data from context
        if (event.contexts) {
          delete event.contexts['localStorage'];
          delete event.contexts['sessionStorage'];
        }
        
        // Remove email addresses from user context
        if (event.user && event.user.email) {
          event.user.email = '[REDACTED]';
        }
        
        return event;
      },
      
      // Ignore errors from browser extensions
      ignoreErrors: [
        // Browser extension errors
        'top.GLOBALS',
        'atomicFindClose',
        'canvas.contentDocument',
        // Chrome extension errors
        'chrome-extension://',
        'moz-extension://',
        // Non-error promise rejections
        'Non-Error promise rejection captured',
        // Third-party script errors
        /^Script error\.?$/,
        /^Javascript error: Script error\.? on line 0$/,
      ],
      
      // Ignore errors from browser extensions and third-party scripts
      denyUrls: [
        /extensions\//i,
        /^chrome:\/\//i,
        /^chrome-extension:\/\//i,
        /^moz-extension:\/\//i,
      ],
    });

    sentryInitialized = true;
    logger.info('Sentry initialized successfully', { 
      environment: config.environment, 
      release: config.release 
    });
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    logger.error('Failed to initialize Sentry', { error: err.message });
  }
}

/**
 * Capture an exception to Sentry
 */
export function captureException(error: Error, context: ErrorContext = {}): string | undefined {
  if (!sentryInitialized) {
    logger.debug('Sentry not initialized, logging error locally', { error, context });
    return;
  }

  try {
    const eventId = Sentry.captureException(error, {
      contexts: {
        custom: context
      }
    });
    return eventId;
  } catch (e) {
    const err = e as Error;
    logger.error('Failed to capture exception to Sentry', { error: err.message });
    return undefined;
  }
}

/**
 * Capture a message to Sentry
 */
export function captureMessage(
  message: string, 
  level: Sentry.SeverityLevel = 'info', 
  context: ErrorContext = {}
): void {
  if (!sentryInitialized) {
    logger.debug('Sentry not initialized, logging message locally', { message, level, context });
    return;
  }

  try {
    Sentry.captureMessage(message, {
      level,
      contexts: {
        custom: context
      }
    });
  } catch (e) {
    const err = e as Error;
    logger.error('Failed to capture message to Sentry', { error: err.message });
  }
}

/**
 * User information for Sentry
 */
export interface SentryUser {
  /** User ID */
  id?: string;
  /** User email (will be redacted) */
  email?: string;
  [key: string]: unknown;
}

/**
 * Set user context for error tracking
 */
export function setUser(user: SentryUser | null): void {
  if (!sentryInitialized) {
    return;
  }

  try {
    Sentry.setUser(user);
  } catch (e) {
    const err = e as Error;
    logger.error('Failed to set Sentry user', { error: err.message });
  }
}

/**
 * Clear user context
 */
export function clearUser(): void {
  if (!sentryInitialized) {
    return;
  }

  try {
    Sentry.setUser(null);
  } catch (e) {
    const err = e as Error;
    logger.error('Failed to clear Sentry user', { error: err.message });
  }
}

/**
 * Breadcrumb data for debugging
 */
export interface SentryBreadcrumb {
  /** Breadcrumb message */
  message?: string;
  /** Category (e.g., 'navigation', 'user-action') */
  category?: string;
  /** Severity level */
  level?: Sentry.SeverityLevel;
  /** Additional data */
  data?: Record<string, unknown>;
}

/**
 * Add breadcrumb for debugging
 */
export function addBreadcrumb(breadcrumb: SentryBreadcrumb): void {
  if (!sentryInitialized) {
    return;
  }

  try {
    Sentry.addBreadcrumb(breadcrumb);
  } catch (e) {
    const err = e as Error;
    logger.error('Failed to add Sentry breadcrumb', { error: err.message });
  }
}

export default {
  initSentry,
  captureException,
  captureMessage,
  setUser,
  clearUser,
  addBreadcrumb
};
